import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '../components/Layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../api/client';
import { Save, KeyRound, Building2, Eye, EyeOff, UserRound, ShieldCheck } from 'lucide-react';
// import { MfaInboxSection } from '../components/Settings/MfaInboxSection';
import { AccessPolicySection } from '../components/Settings/AccessPolicySection';
import { PageHeader } from '../components/common/PageHeader';
// import { secaoDoCodigoPorEmailVisivel } from '../utils/mfaInbox';
import { nomeDoPapel, nomeOuEmail } from '../utils/nomes';

// M7: abas em vez de quatro blocos soltos numa página longa. A aba vai no endereço (?aba=...),
// para o menu da conta abrir direto na certa.
type Aba = 'perfil' | 'organizacao' | 'acesso';

const estiloDoCampo: React.CSSProperties = { background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', width: '100%' };
const estiloDoRotulo: React.CSSProperties = { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' };
const estiloDoCartao: React.CSSProperties = { padding: 'var(--space-6)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', maxWidth: '640px' };

/** M7: campo de senha com mostrar/ocultar, como no modal de credencial. */
function CampoSenha({ id, rotulo, valor, aoMudar, minLength, autoComplete }: { id: string; rotulo: string; valor: string; aoMudar: (v: string) => void; minLength?: number; autoComplete: string }) {
  const [visivel, setVisivel] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <label htmlFor={id} style={estiloDoRotulo}>{rotulo}</label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={visivel ? 'text' : 'password'}
          value={valor}
          onChange={e => aoMudar(e.target.value)}
          required
          minLength={minLength}
          autoComplete={autoComplete}
          style={{ ...estiloDoCampo, paddingRight: '48px' }}
        />
        <button
          type="button"
          className="botao-icone botao-icone--sem-borda"
          onClick={() => setVisivel(v => !v)}
          aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          title={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)' }}
        >
          {visivel ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
}

function CaixaDeStatus({ status }: { status: { type: 'success' | 'error'; msg: string } | null }) {
  if (!status) return null;
  return (
    <div role={status.type === 'error' ? 'alert' : 'status'} style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', background: status.type === 'success' ? 'var(--color-success-muted)' : 'var(--color-error-muted)', color: status.type === 'success' ? 'var(--color-success)' : 'var(--color-error)' }}>
      {status.msg}
    </div>
  );
}

function TituloDoCartao({ icone, titulo, descricao }: { icone: React.ReactNode; titulo: string; descricao: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
      <div style={{ background: 'var(--color-bg-elevated)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', display: 'inline-flex' }}>
        {icone}
      </div>
      <div>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)' }}>{titulo}</h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{descricao}</p>
      </div>
    </div>
  );
}

export function Settings() {
  const { user, membership, loadUser } = useAuth();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';
  const [searchParams, setSearchParams] = useSearchParams();

  const abas: { id: Aba; rotulo: string; icone: React.ReactNode }[] = [
    { id: 'perfil', rotulo: 'Perfil', icone: <UserRound size={16} /> },
    ...(isAdmin ? [
      { id: 'organizacao' as const, rotulo: 'Organização', icone: <Building2 size={16} /> },
      { id: 'acesso' as const, rotulo: 'Regras de acesso', icone: <ShieldCheck size={16} /> },
    ] : []),
  ];
  const pedida = searchParams.get('aba');
  const abaAtual: Aba = abas.some(a => a.id === pedida) ? (pedida as Aba) : 'perfil';
  const trocarAba = (aba: Aba) => setSearchParams(aba === 'perfil' ? {} : { aba }, { replace: true });

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [orgName, setOrgName] = useState('');
  const [orgStatus, setOrgStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null);
  const [isSavingOrg, setIsSavingOrg] = useState(false);

  useEffect(() => {
    if (membership?.org_name) {
      setOrgName(membership.org_name);
    }
  }, [membership]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', msg: 'A nova senha e a confirmação não coincidem.' });
      return;
    }

    setIsChangingPassword(true);
    setPasswordStatus(null);
    try {
      await apiClient.patch('/users/me/password', { current_password: currentPassword, new_password: newPassword });
      setPasswordStatus({ type: 'success', msg: 'Senha alterada com sucesso!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordStatus({ type: 'error', msg: err.message || 'Erro ao alterar a senha.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleUpdateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingOrg(true);
    setOrgStatus(null);
    try {
      await apiClient.patch('/orgs/me', { name: orgName });
      setOrgStatus({ type: 'success', msg: 'Organização atualizada com sucesso!' });
      await loadUser(); // Refresh membership data
    } catch (err: any) {
      setOrgStatus({ type: 'error', msg: err.message || 'Erro ao atualizar a organização.' });
    } finally {
      setIsSavingOrg(false);
    }
  };

  return (
    <AppLayout>
      <PageHeader title="Configurações" description="Gerencie seu perfil e as configurações da organização." />

      {abas.length > 1 && (
        <div role="tablist" aria-label="Seções das configurações" className="abas">
          {abas.map(aba => (
            <button
              key={aba.id}
              type="button"
              role="tab"
              id={`aba-${aba.id}`}
              aria-selected={abaAtual === aba.id}
              aria-controls={`painel-${aba.id}`}
              onClick={() => trocarAba(aba.id)}
              className="aba"
            >
              {aba.icone} {aba.rotulo}
            </button>
          ))}
        </div>
      )}

      <div role="tabpanel" id={`painel-${abaAtual}`} aria-labelledby={abas.length > 1 ? `aba-${abaAtual}` : undefined} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
        {abaAtual === 'perfil' && (
          <>
            <div className="glass" style={estiloDoCartao}>
              <TituloDoCartao icone={<UserRound size={24} color="var(--color-primary-400)" />} titulo="Sua conta" descricao="Os dados com que você entra no Lictor" />
              <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) minmax(0, 1fr)', gap: 'var(--space-2) var(--space-6)', fontSize: 'var(--text-sm)' }}>
                <dt style={{ color: 'var(--color-text-secondary)' }}>Nome</dt>
                <dd style={{ overflowWrap: 'anywhere' }}>{nomeOuEmail(user?.name, user?.email)}</dd>
                <dt style={{ color: 'var(--color-text-secondary)' }}>E-mail</dt>
                <dd style={{ overflowWrap: 'anywhere' }}>{user?.email}</dd>
                <dt style={{ color: 'var(--color-text-secondary)' }}>Papel</dt>
                <dd>{nomeDoPapel(membership?.role)}{membership?.org_name ? ` em ${membership.org_name}` : ''}</dd>
              </dl>
            </div>

            <div className="glass" style={estiloDoCartao}>
              <TituloDoCartao icone={<KeyRound size={24} color="var(--color-primary-400)" />} titulo="Alterar senha" descricao="Atualize sua senha de acesso" />
              <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <CampoSenha id="senha-atual" rotulo="Senha atual" valor={currentPassword} aoMudar={setCurrentPassword} autoComplete="current-password" />
                <CampoSenha id="senha-nova" rotulo="Nova senha" valor={newPassword} aoMudar={setNewPassword} minLength={8} autoComplete="new-password" />
                <CampoSenha id="senha-confirmacao" rotulo="Confirme a nova senha" valor={confirmPassword} aoMudar={setConfirmPassword} minLength={8} autoComplete="new-password" />
                <div style={{ paddingTop: 'var(--space-2)' }}>
                  <CaixaDeStatus status={passwordStatus} />
                  <button type="submit" disabled={isChangingPassword} className="btn btn--primary">
                    <Save size={18} /> {isChangingPassword ? 'Salvando...' : 'Salvar nova senha'}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {abaAtual === 'organizacao' && isAdmin && (
          <>
            <div className="glass" style={estiloDoCartao}>
              <TituloDoCartao icone={<Building2 size={24} color="var(--color-primary-400)" />} titulo="Organização" descricao="Configurações do espaço de trabalho" />
              <form onSubmit={handleUpdateOrg} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <label htmlFor="nome-da-organizacao" style={estiloDoRotulo}>Nome da organização</label>
                  <input id="nome-da-organizacao" type="text" value={orgName} onChange={e => setOrgName(e.target.value)} required style={estiloDoCampo} />
                </div>
                <div style={{ paddingTop: 'var(--space-2)' }}>
                  <CaixaDeStatus status={orgStatus} />
                  <button type="submit" disabled={isSavingOrg} className="btn btn--primary">
                    <Save size={18} /> {isSavingOrg ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>
              </form>
            </div>

            {/* Etapa 13: código de verificação por e-mail (só owner/admin e só com o recurso liberado).
                Escondido por enquanto (Léo, 03/11/2026): os códigos por e-mail ainda não estão
                implementados. Para voltar a mostrar, é só descomentar a linha abaixo e os dois imports. */}
            {/* {secaoDoCodigoPorEmailVisivel(membership) && <MfaInboxSection />} */}
          </>
        )}

        {/* Etapa 18: regras de acesso (horário, IP) e sites bloqueados (só owner/admin) */}
        {abaAtual === 'acesso' && isAdmin && <AccessPolicySection />}
      </div>
    </AppLayout>
  );
}
